import React, { useCallback } from 'react';
import { LicenseMappingTable, LicenseMappingTableLabels } from './LicenseMappingTable';
import { License, LicenseUser, Module, Product } from './types';

/**
 * @template {import('./types').Product} P
 * @template {import('./types').License} L
 * @template {import('./types').Module} M
 * @template {import('./types').LicenseUser} T
 * @typedef {{
 *   license: L,
 *   products: P[],
 *   licenseUsers: T[],
 *   id: string,
 *   sortable?: boolean,
 *   updateLicenseMapping: (licenseId: string) => (data: T[]) => void,
 *   header?: React.ReactChild,
 *   table?: boolean,
 *   editable?: boolean,
 *   placeholder?: string,
 * } & import('./LicenseMappingTable').LicenseMappingTableLabels} LicenseMappingProps
 */

export interface LicenseMappingProps<L extends License, P extends Product, M extends Module, U extends LicenseUser> extends LicenseMappingTableLabels {
    license: L;
    products: P[];
    licenseUsers: U[];
    id: string;
    sortable?: boolean;
    updateLicenseMapping: (licenseId: string) => (data: U[]) => void;
    header?: React.ReactNode;
    table?: boolean;
    editable?: boolean;
    placeholder?: string;
}

export const LicenseMapping = <
    L extends License,
    P extends Product,
    M extends Module,
    U extends LicenseUser
>(props: LicenseMappingProps<L, P, M, U>): JSX.Element => {
    const availableProducts = props.products.filter(product => props.license.modules.indexOf(product.id) >= 0);

    const handleChange = useCallback((items: U[]) => {
        props.updateLicenseMapping(props.license.uuid)(items);
    }, [ props.updateLicenseMapping, props.license ]);

    const setModulesForLicenseUser = useCallback((licenseUser: U) =>
        (activeModules: M[]) => {
            const newData = props.licenseUsers
                .map(user => user.id === licenseUser.id ? { ...user, activeModules } : user);
            handleChange(newData);
        }, [ handleChange, props.licenseUsers ]);

    const handleLicenseUserRemove = useCallback((userId: string) => (): void => {
        const userIds = typeof userId === 'object' ? userId : [ userId ];
        handleChange(props.licenseUsers.filter(user => userIds.indexOf(user.id) < 0));
    }, [ handleChange, props.licenseUsers ]);

    return (
        <>
            {props.header}
            <LicenseMappingTable
                products={availableProducts}
                license={props.license}
                licenseUsers={props.licenseUsers}
                handleReorder={handleChange}
                handleLicenseUserRemove={handleLicenseUserRemove}
                setModulesForLicenseUser={setModulesForLicenseUser}
                id={props.id}
                orderable={props.editable && props.sortable}
                editable={props.editable}
                getSelectionStatusLabel={props.getSelectionStatusLabel}
                licenseUserLabel={props.licenseUserLabel}
                licenseUserRemoveLabel={props.licenseUserRemoveLabel}
                selectAllLabel={props.selectAllLabel}
                rowActionLabel={props.rowActionLabel}
                dragHandleLabel={props.dragHandleLabel}
                productEnabledLabel={props.productEnabledLabel}
                productDisabledLabel={props.productDisabledLabel}
                placeholder={props.placeholder}
            />
        </>
    );
};
